import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NavBar } from '../kuroco/AppBars';
import { getInquiry, sendInquiryMessage } from '../../api/inquiriesApi';
import { devStatusOf, formatTime, groupByDay, speakerLabel, statusOf } from '../../utils/inquiryView';
import { MAX_BODY } from './InquiryForm';
import '../../styles/inquiry.css';

const USER_API = { load: getInquiry, send: sendInquiryMessage };

/**
 * お問い合わせ 1 件のやり取り（チャットのような見せ方）。自分の発言は右・緑、相手は左・白。
 * 既定は本人用（viewer="user"）: 追記すると、開発者にメールが届く。開くと、返信を読んだ印が付く（サーバー側）。
 * 開発者用（viewer="dev"）は、api・戻り先・先頭に出す情報（extraHeader）を差し替えて、返信する（DevInquiryThread）。
 *   extraHeader(thread, setThread) → 送った人の情報や、対応済みの切り替えなど。
 */
export default function InquiryThread({
  viewer = 'user',
  api = USER_API,
  backTo = '/settings/help',
  backLabel = 'ヘルプ',
  extraHeader = null,
  placeholder = '追記する内容を入力',
}) {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const bottomRef = useRef(null);
  const isDev = viewer === 'dev';

  useEffect(() => {
    let cancelled = false;
    setThread(null);
    setLoadError('');
    api
      .load(id)
      .then((res) => !cancelled && setThread(res))
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setLoadError('開発者のみ利用できます');
        else setLoadError(err.status === 404 ? 'お問い合わせが見つかりません' : '読み込めませんでした');
      });
    return () => {
      cancelled = true;
    };
    // api は、呼び出し側が固定のオブジェクトを渡す（id が変わったときだけ、取り直す）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [thread?.messages?.length]);

  const send = async () => {
    setSending(true);
    setSendError('');
    try {
      const added = await api.send(id, text.trim());
      setThread((t) => ({ ...t, lastMessageBy: viewer, messages: [...t.messages, added] }));
      setText('');
    } catch (err) {
      setSendError(err.message || '送信に失敗しました。時間をおいて、もう一度お試しください');
    } finally {
      setSending(false);
    }
  };

  const title = thread ? `お問い合わせ ${thread.numberLabel}` : 'お問い合わせ';
  const st = thread ? (isDev ? devStatusOf(thread) : statusOf(thread)) : null;
  const canSend = text.trim().length > 0 && !sending;

  return (
    <div className="app">
      <NavBar title={title} backLabel={backLabel} backTo={backTo} hideSettings />
      <div className="scroll fade inq-thread">
        {loadError && <div className="inq-empty">{loadError}</div>}
        {!thread && !loadError && <div className="inq-empty">読み込み中…</div>}
        {thread && (
          <>
            {extraHeader?.(thread, setThread)}
            <div className="inq-head">
              <span className="inq-cat">{thread.category}</span>
              <span className={`inq-status inq-status--${st.key}`}>{st.label}</span>
            </div>
            {groupByDay(thread.messages).map((g) => (
              <div key={g.label}>
                <div className="inq-day">{g.label}</div>
                {g.messages.map((m) => (
                  <div key={m.id} className={`inq-msg inq-msg--${m.from === viewer ? 'mine' : 'theirs'}`}>
                    <div className="inq-who">{speakerLabel(m.from, viewer, thread.author?.name)}</div>
                    <div className="inq-bubble">{m.body}</div>
                    <div className="inq-time">{formatTime(m.createdAt)}</div>
                  </div>
                ))}
              </div>
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>
      {thread && (
        <div className="inq-composer">
          {sendError && (
            <div className="help-error" role="alert">
              {sendError}
            </div>
          )}
          <div className="inq-composer-row">
            <textarea
              className="inq-input"
              rows={2}
              value={text}
              maxLength={MAX_BODY}
              placeholder={placeholder}
              aria-label={isDev ? '返信する内容' : '追記する内容'}
              onChange={(e) => setText(e.target.value)}
            />
            <button type="button" className="inq-send" disabled={!canSend} onClick={send}>
              {sending ? '送信中…' : '送信'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
